/* eslint-disable eqeqeq */
/* eslint-disable no-template-curly-in-string */
import React, { useEffect } from 'react';
import {
  array, boolean, number, object, string,
} from 'yup';
import _, {
  castArray, cloneDeep, find, omit,
} from 'lodash';
import Tabs from 'react-bootstrap/Tabs';
import Tab from 'react-bootstrap/Tab';

import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons/faDownload';

import Alert from '~/commons/components/Alert.jsx';
import FormGroup from '~/commons/components/FormGroup.jsx';
import FormCheckGroup from '~/commons/components/FormCheckGroup.jsx';
import { env } from '~/commons/config';

import qs from 'qs';
import useHttpStatus from '~/commons/hooks/useHttpStatus';
import { useLocation } from 'react-router-dom';
import FormResource from './FormResource.jsx';
import FieldListSelect from './FieldListSelect.jsx';
import FieldArrayCards from './FieldArrayCards.jsx';
import FormDesignTFY from './FormDesignTFY.jsx';
import { tradeDiscountStr, commaSeparatedToNumbers } from './utils';
import { tfyMeasureUnits } from '../../../../../../components/core/models/commons';

const byRRP = (value) => env.currencies.reduce((acc, c) => ({ ...acc, [c.code]: value }), {});
const variSchema = (tpCurrency) => object({
  name: string(),
  sizing: string().required(),
  colorway: string(),
  itemCode: string().required(),
  rrp: object(byRRP(number())),
  tradeRrp: object(byRRP(number())),
  width: number(),
  length: number(),
  arImg: string(),
  arModel: string(),
}).test({
  test: ({ rrp, tradeRrp }) => {
    for (const { code } of env.currencies) {
      if (_.includes(tpCurrency, code) && rrp[code] > 0 && (tradeRrp[code] ?? '') === '') return false;
    }
    return true;
  },
  message: 'Trade price is a required field',
}).test({
  test: ({ rrp, tradeRrp }) => {
    for (const { code } of env.currencies) {
      if (_.includes(tpCurrency, code) && rrp[code] > 0 && tradeRrp[code] == 0) return false;
    }
    return true;
  },
  message: 'Invalid trade price',
}).test({
  test: ({ rrp, tradeRrp }) => {
    for (const { code } of env.currencies) {
      if (_.includes(tpCurrency, code) && tradeRrp[code] > rrp[code]) return false;
    }
    return true;
  },
  message: 'Please check TP Price cannot greater than RP Price',
});

const blueprintSchema = object({
  manualApproval: boolean(),
  material: string(),
  numberOfLayers: number().required().min(1),
  priceColor: number().nullable(),
  priceShape: number().nullable(),
  supplier: object({
    _id: string().required('Supplier is a required field'),
  }),
  currency: string().required(),
  unitPrice: object({
    retailPrice: number().required().test(
      'decimal-places',
      'Unit price only accepts 2 decimal places',
      (val) => _.isNumber(val) && /^(?:0|\d*(\.\d{1,2})?)$/.test(val.toString()),
    ),
    unit: string().required(),
  }),
  layerInfo: array().of(object({
    cutting: string(),
    material: string(),
    defaultColor: string(),
    patternImg: string(),
  })).when(['numberOfLayers', 'standardSize'], (numberOfLayers, standardSize, s) => {
    if (_.every(standardSize, (x) => !x.itemForTFY)) return s;
    return s.test(
      'defaultColor',
      '${path} Default color is a required field',
      (layerInfo) => _.every(_.range(0, numberOfLayers), (i) => !_.isEmpty(layerInfo[i]?.defaultColor)),
    );
  }).when(['numberOfLayers', 'patternEnable'], (numberOfLayers, patternEnable, s) => s.test(
    'patternImg',
    '${path} Image is a required field',
    (layerInfo) => (patternEnable ? _.every(_.range(0, numberOfLayers), (i) => !_.isEmpty(layerInfo[i]?.patternImg)) : s),
  )),
  paletteName: string(),
  palette: array().of(object({
    _id: string().required(),
  })).required(),
  colorEnable: boolean(),
  patternEnable: boolean(),
  patternInfo: object().when('patternEnable', {
    is: true,
    then: () => object({
      gdn: string().required(),
      unitWidth: number().required(),
      unitLength: number().required(),
      defaultWidth: number().required(),
      defaultLength: number().required(),
      unit: string().oneOf(tfyMeasureUnits).required(),
      fillMode: string().required(),
    }),
  }),
  standardSize: array().when('patternEnable', {
    is: (v) => v !== true,
    then: () => array().of(object({
      itemCode: string().required(),
      gdn: string().required(),
      displaySize: string().required(),
      width: number().required(),
      length: number().required(),
      unit: string().oneOf(tfyMeasureUnits).required(),
      retailPrice: number().required(),
      itemForTFY: boolean(),
      itemForStock: boolean(),
      layerInfo: array().of(object({
        img: string(),
        percentage: number().nullable(),
      })).test('percentage', '${path} Sum of all layer should be 100%', (info) => _.every(info, (x) => _.isNil(x.percentage)) || _.sumBy(info, 'percentage') === 100),
      colorways: array().of(object({
        itemCode: string().required(),
        gdn: string().required(),
        name: string().required(),
        colors: array().of(string()),
        retailPrice: number().required(),
      })),
    }).when(['numberOfLayers'], (numberOfLayers, s) => s.test(
      'image',
      '${path} Image is a required field',
      ({ itemForTFY, layerInfo }) => !itemForTFY || (!_.isEmpty(layerInfo) && _.every(_.range(0, numberOfLayers), (i) => !_.isEmpty(layerInfo[i]?.img))),
    )).when(['numberOfLayers'], (numberOfLayers, s) => s.test(
      'color',
      '${path} Color is a required field',
      ({ itemForTFY, layerInfo }) => !itemForTFY || (!_.isEmpty(layerInfo) && _.every(_.range(0, numberOfLayers), (i) => !_.isEmpty(layerInfo[i]?.colors) && _.every(layerInfo[i]?.colors ?? [], (c) => !_.isEmpty(c)))),
    ))),
  }),
  resize: string().oneOf(['none', 'rescale', 'crop']),
  rescaleLimit: number().nullable(),
  cropLimit: number().nullable(),
  shapeEnable: boolean(),
  shapeCustomizationEnable: boolean(),
  shape: array().of(string()).when('shapeEnable', {
    is: true,
    then: (schema) => schema.required(),
  }).when('originalShape', (v, schema) => schema.test('shape', 'Shape should includes the original shape', (l) => !v || _.includes(l, v))),
  originalShape: string(),
});

const schema = (orgIds, tpCurrency) => object({
  design: string(),
  orgId: string().matches(/^(?:\d+,?)+$/, 'Invalid Organization ID Format'),
  coll: object({
    _id: string().required('Collection is a required field'),
    name: string(),
  }),
  useHex: boolean(),
  arActive: boolean(),
  variation: array().of(variSchema(tpCurrency)),
  tfyInfo: object({
    active: boolean(),
    baseItemCode: string(),
    orgId: string().matches(/^(?:\d+,?)+$/, 'Invalid Organization ID Format'),
  }).test('tfyInfo', 'Base Item Code is a required field', (info) => {
    const orgId = commaSeparatedToNumbers(info?.orgId ?? '');
    return _.isEmpty(orgId) || !_.isEmpty(info?.baseItemCode);
  }),
  blueprints: object().when('tfyInfo.orgId', (orgId) => object(_.fromPairs(_.map(commaSeparatedToNumbers(orgId ?? ''), (x) => [x, blueprintSchema])))),
});

const defaults = {
  active: true,
  brand: '',
  coll: { name: '', orgIds: '' },
  design: '',
  gdn: '',
  collaborator: { name: '' },
  construction: '',
  style: '',
  shape: '',
  color: '',
  size: '',
  description: '',
  useHex: false,
  arActive: true,
  variation: [],
  photos: [],
  videos: '',
  orgId: '',
  tfyInfo: {
    active: false,
    baseItemCode: '',
    orgId: '',
    scale: '',
    quality: '',
  },
  blueprints: {},
};

const FormDesign = ({ design }) => {
  const [alert, setAlert] = React.useState();
  const { search, hash } = useLocation();
  const { tab } = qs.parse(search, { ignoreQueryPrefix: true }) ?? {};
  useEffect(() => {
    if (hash) {
      const element = document.getElementById(hash.slice(1));
      if (element) element.scrollIntoView();
    }
  }, [hash]);

  const [resConfig, reqConfig] = useHttpStatus();
  const [resSR, reqSR] = useHttpStatus();
  useEffect(() => {
    reqConfig({
      url: '/api/site/configs',
    });
  }, []);
  useEffect(() => {
    reqSR({ url: '/api/core/users/self/showrooms' });
  }, []);
  const { tradePriceOrgs } = resConfig?.payload ?? {};
  const tpCurrency = _.flatMap(resSR?.payload, (x) => (_.includes(tradePriceOrgs, x.orgId) ? x.currency : []));
  const orgIds = _.uniq(_.map(resSR?.payload, (x) => x.orgId));

  return (
    <FormResource
      {...{
        defaults,
        schema: schema(orgIds, tpCurrency),
        segment: 'designs',
        initialValuesOpts: { omitIds: false },
        stored: design
          ? (() => {
            const form = cloneDeep(design);
            form.description = form.description ?? '';
            ['style', 'shape', 'color', 'size', 'orgId', 'videos', 'tfyInfo.orgId'].forEach((key) => {
              _.set(form, key, castArray(_.get(form, key))
                ?.map((s) => (typeof s === 'string' ? s.trim() : s))
                .filter(Boolean)
                .join(',') ?? JSON.stringify(_.get(form, key)));
            });
            return form;
          })()
          : undefined,
        api: '/api/core/designs',
        onSubmit: (v) => {
          const form = cloneDeep(v);
          ['style', 'shape', 'color', 'videos'].forEach((key) => {
            form[key] = form[key].split(',').map((s) => s.trim());
            v[key] = form[key].join(',');
          });
          delete form.size;

          form.orgId = commaSeparatedToNumbers(form.orgId);
          v.orgId = form.orgId.join(',');

          if (form.tfyInfo) {
            form.tfyInfo.orgId = commaSeparatedToNumbers(form.tfyInfo.orgId);
            v.tfyInfo.orgId = form.tfyInfo.orgId.join(',');
          }

          if (!form?.collaborator?._id) form.collaborator = null;

          return form;
        },
      }}>
      {({ values, setValues }) => (
        <>
          {alert && (
            <Alert theme={alert.type} onClose={() => setAlert()}>
              {_.isString(alert.message) ? alert.message : JSON.stringify(alert.message)}
            </Alert>
          )}
          <Tabs defaultActiveKey={tab ?? 'basic'} id="design-tabs" unmountOnExit>
            <Tab eventKey="basic" title="Basic">
              <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">BASIC</h6>
              <div className="row">
                <div className="col">
                  <div className="form-group">
                    <label>Active</label>
                    <FormCheckGroup name="active" type="checkbox" label="" asSwitch />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormGroup name="brand" />
                </div>
                <div className="col">
                  <FormGroup name="coll.name" label="Collection" disabled />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormGroup name="design" />
                </div>
                <div className="col">
                  <FormGroup name="gdn" label="GDN" />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormGroup name="construction" />
                </div>
                <div className="col">
                  <FormGroup name="collaborator.name" label="Collaborator" disabled />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <FormGroup name="orgId" label="Organization ID (Comma separated)" />
                </div>
              </div>
              <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">DETAILS</h6>
              <FormGroup name="style" label="Style (Comma separated)" />
              <FormGroup name="shape" label="Shape (Comma separated)" />
              <FormGroup name="color" label="Color (Comma separated)" />
              <FormGroup name="size" label="Size (Comma separated)" disabled />
              <FormGroup name="videos" label="Video (Comma separated)" />
              <FormGroup
                name="photos"
                as="slider"
                api="/api/core/designs/files"
                label="Photos"
                gridClassName="col-3 my-3"
                slideClassName="file-slide-contain square"
              />
              <FormGroup
                name="description"
                as="summernote"
                options={{
                  styleTags: ['p', 'h2', 'h3', 'h4', 'h5', 'h6'],
                  toolbar: [
                    ['style', ['style']],
                    ['font', ['bold', 'italic', 'underline', 'strikethrough', 'clear']],
                    ['view', ['codeview', 'undo', 'redo']],
                    ['para', ['paragraph']],
                  ],
                }}
              />
            </Tab>
            <Tab eventKey="coll" title="Collection">
              <FieldListSelect
                {...{
                  name: 'coll',
                  list: {
                    segment: 'collections',
                    api: '/api/core/collections',
                    sort: { name: 1 },
                    cols: [
                      { key: 'name', sortable: true },
                      { key: 'orgIds', children: 'Organizations', getter: (v) => (v || []).join(', ') },
                    ],
                  },
                }}>
                <>
                  <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">Collection</h6>
                  <div className="row">
                    <div className="col">
                      <FormGroup name="coll.name" label="Collection" disabled />
                    </div>
                    <div className="col">
                      <FormGroup
                        name="coll.orgIds"
                        label="Organization ID (Comma separated)"
                        as="display"
                      />
                    </div>
                  </div>
                </>
              </FieldListSelect>
            </Tab>
            <Tab eventKey="collaborator" title="Collaborator">
              <FieldListSelect
                {...{
                  name: 'collaborator',
                  list: {
                    segment: 'collaborators',
                    api: '/api/core/collaborators',
                    sort: { name: 1 },
                    cols: [{ key: 'name', sortable: true }],
                  },
                }}>
                {({ setValues }) => (
                  <>
                    <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">Collaborator</h6>
                    <div className="row">
                      <div className="col">
                        <FormGroup name="collaborator.name" label="Collaborator" disabled />
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary mb-3"
                        onClick={() => setValues((val) => ({
                          ...val,
                          collaborator: { name: '' },
                        }))
                        }>
                        Clear
                      </button>
                    </div>
                  </>
                )}
              </FieldListSelect>
            </Tab>
            <Tab eventKey="variants" title="Variants">
              <h6 className="small border-bottom text-muted py-1 pt-4 mb-3">Variants</h6>
              <div className="row">
                <div className="col d-flex mb-2">
                  <label>Use Colorway</label>
                  <div className="ml-2">
                    <FormCheckGroup
                      name="useHex"
                      type="checkbox"
                      label=""
                      className="both-active"
                      asSwitch
                    />
                  </div>
                  <label>Use Color Dot</label>
                </div>
                <div className="col d-flex mb-2">
                  <label>AR</label>
                  <div className="ml-2">
                    <FormCheckGroup
                      name="arActive"
                      type="checkbox"
                      label=""
                      asSwitch
                    />
                  </div>
                </div>
              </div>

              <FieldArrayCards
                {...{
                  name: 'variation',
                  push: {
                    name: '',
                    sizing: '',
                    colorway: '',
                    itemCode: '',
                    hexcode: '#ffffff',
                    rrp: byRRP(0),
                    tradeRrp: byRRP(0),
                    arImg: '',
                    arModel: '',
                  },
                }}>
                {(r, i) => (
                  <>
                    <div id={r.itemCode} className="row">
                      <div className="col">
                        <FormGroup name={`variation.${i}.name`} label="Variation Name" />
                      </div>
                      <div className="col">
                        <FormGroup name={`variation.${i}.sizing`} label="Sizing" />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <FormGroup name={`variation.${i}.colorway`} label="Colorway" />
                      </div>
                      <div className="col">
                        <FormGroup name={`variation.${i}.hexcode`} label="Color Dot" type="color" />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <FormGroup name={`variation.${i}.width`} type="number" label="Width" />
                      </div>
                      <div className="col">
                        <FormGroup name={`variation.${i}.length`} type="number" label="Length" />
                      </div>
                    </div>
                    <div className="row">
                      {env.currencies.map((c) => (
                        <div className="col" key={`variation.${i}.rrp.${c.code}`}>
                          <FormGroup
                            name={`variation.${i}.rrp.${c.code}`}
                            label={`${c.code}(${c.symbol}) RP`}
                            type="number"
                            min={0}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="row">
                      {env.currencies.map((c) => (
                        <div className="col" key={`variation.${i}.tradeRrp.${c.code}`}>
                          <FormGroup
                            name={`variation.${i}.tradeRrp.${c.code}`}
                            label={`${c.code}(${c.symbol}) TP`}
                            type="number"
                            min={0}
                            helpText={tradeDiscountStr(r.rrp[c.code] ?? 0, r.tradeRrp[c.code] ?? 0, !_.includes(tpCurrency, c.code))}
                          />
                        </div>
                      ))}
                    </div>
                    <div className="row">
                      <div className="col">
                        <FormGroup name={`variation.${i}.itemCode`} label="Item Code" />
                      </div>
                      <div className="col" />
                    </div>
                    <div className="row">
                      <div className="col">
                        <FormGroup
                          name={`variation.${i}.arImg`}
                          as="coverImg"
                          api="/api/core/designs/files"
                          label="AR Image"
                        />
                      </div>
                      <div className="col">
                        <FormGroup name={`variation.${i}.arModel`} label="AR Model" as="select">
                          <option value=""> - </option>
                          {env.arModels.map((v) => (
                            <option value={v} key={v}>{v}</option>
                          ))}
                        </FormGroup>
                      </div>
                      <div className="col">
                        {r.arImg && r.arModel && (
                          <div className="form-group">
                            <label>AR Download</label>
                            <div>
                              <a
                                className="btn btn-secondary mr-2"
                                href={`/api/core/arModels/${r.arModel}/${_.trim(r.arImg, '/').replace('uploads/', '')}/result.usdz`}
                              >
                                <FA icon={faDownload} fixedWidth />
                              </a>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </FieldArrayCards>
            </Tab>
            <Tab eventKey="tfy" title="TFY">
              <FormDesignTFY values={values} setValues={setValues} setAlert={setAlert} />
            </Tab>
          </Tabs>
        </>
      )}
    </FormResource>
  );
};

export default FormDesign;
